import { Box, Button, Flex, Loading, Text, createColumnHelper } from 'ui'
import { useMemo } from 'react'
import { usePulseContext } from '../../context/PulseProvider'
import { useSpendByLocation } from 'queries/pulse/useSpendByLocation'
import { SpendByLocation as SpendByLocationType } from 'api/pulse/spendByLocation'
import { formatCurrency } from 'lib/string'
import { InfiniteTable } from '../InfiniteTable'
import { useSpendByLocationDownloadCsv } from 'queries/pulse/useSpendByLocationDownloadCsv'
import { formatTimePeriodDate } from '../../utils'

const columnHelper = createColumnHelper<SpendByLocationType[0]>()

export function SpendByLocation() {
  const {
    state: { filters },
  } = usePulseContext()

  const filtersWithPagination = useMemo(() => {
    return {
      ...filters,
      page: 0,
      pageSize: 50,
    }
  }, [filters])

  const { data, isLoading, isFetching, fetchNextPage, hasNextPage } =
    useSpendByLocation(filtersWithPagination)

  const { mutateAsync: downloadCsv, isPending: isDownloadingCsv } =
    useSpendByLocationDownloadCsv(filters)

  const flatData = useMemo(() => {
    return data?.pages.flatMap((page) => page) ?? []
  }, [data])

  const columns = [
    columnHelper.accessor('revenue_date', {
      header: 'Revenue Date',
      cell: (info) => (
        <Text>
          {formatTimePeriodDate(filters.timePeriod, info.getValue() as string)}
        </Text>
      ),
    }),
    columnHelper.accessor('company_name', {
      header: 'Location Name',
    }),
    columnHelper.accessor('spend_category', {
      header: 'Spend Category',
      cell: (info) => <Text>{info.getValue()}</Text>,
    }),
    columnHelper.accessor('total_spend', {
      header: 'Total Spend',
      cell: (info) => (
        <Text>
          {typeof info.getValue() === 'number'
            ? formatCurrency(info.getValue() as number)
            : 'N/A'}
        </Text>
      ),
    }),
  ]

  return (
    <Box>
      <Flex
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
        gap={3}
        mb={3}
      >
        <Box>
          <Text fontSize={3} fontWeight="500">
            Spend Data Breakdown - by Location
          </Text>
          <Text fontSize={2} color="lightText">
            Breakdown of spend by category for each location
          </Text>
        </Box>
        <Button
          variant="secondary"
          onClick={() => downloadCsv()}
          loading={isDownloadingCsv}
        >
          CSV
        </Button>
      </Flex>
      {isLoading ? (
        <Box p={3}>
          <Loading />
        </Box>
      ) : (
        <InfiniteTable
          data={flatData}
          columns={columns}
          isFetching={isFetching}
          fetchNextPage={fetchNextPage}
          hasNextPage={hasNextPage}
          height="400px"
        />
      )}
    </Box>
  )
}
